/**
 * Utility functions for transforming form data to backend API format
 */

export interface VendorFormData {
  firstName: string;
  lastName: string;
  personalEmail?: string;
  washCenterName: string;
  businessEmail: string;
  opensAt: string;
  closesAt: string;
  phoneNumber: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  country?: string;
  gstNumber: string;
  panNumber: string;
  aadharNumber: string;
  location: string;
  panFile?: File;
  aadharFile?: File;
  latitude?: string;
  longitude?: string;
  pincode?: string;
  bankName: string;
  bankAccountNumber: string;
  ifscCode: string;
  accountType: string;
  branchName: string;
  hasSecondaryAccount: boolean;
  secondaryBankName?: string;
  secondaryBankAccountNumber?: string;
  secondaryIfscCode?: string;
  secondaryAccountType?: string;
  secondaryBranchName?: string;
}

export interface BackendVendorData {
  personalDetails: {
    fname: string;
    lname: string;
    phone_no?: string;
    email?: string;
  };
  businessDetails: {
    business_name: string;
    contact_no: string;
    contact_email: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    country: string;
    pincode: string;
    longitude: number;
    latitude: number;
    opens_at: string;
    closes_at: string;
    timeslot_duration: number;
    simultenous_booking: number;
    weekly_holidays: number[];
    holidays: string[];
    unavailable_timeslots: number[];
  };
  kycDetails: {
    pan_no: string;
    aadhaar_no: string;
    gst_no: string;
  };
  bankAccountDetails: Array<{
    account_number: string;
    ifsc_code: string;
    upi_id?: string;
    account_holder_name?: string;
    bank_name?: string;
    branch_name?: string;
    is_primary: boolean;
  }>;
}

/**
 * Normalizes common IFSC entry mistakes. The fifth character in every Indian
 * IFSC code is the digit 0, so users frequently enter the letter O there.
 */
export function normalizeIfscCode(value: string): string {
  const normalized = value.toUpperCase().replace(/\s/g, '');

  if (normalized.length >= 5 && normalized[4] === 'O') {
    return `${normalized.slice(0, 4)}0${normalized.slice(5)}`;
  }

  return normalized;
}

/**
 * Converts 12-hour time format to 24-hour format
 * @param time12h - Time in 12-hour format (e.g., "03:10 PM")
 * @returns Time in 24-hour format (e.g., "15:10")
 */
export function convertTo24Hour(time12h: string): string {
  const [time, modifier] = time12h.split(' ');
  let [hours, minutes] = time.split(':');

  if (hours === '12') {
    hours = '00';
  }

  if (modifier === 'PM') {
    hours = (parseInt(hours, 10) + 12).toString();
  }

  return `${hours.padStart(2, '0')}:${minutes}`;
}

/**
 * Generates a default email from name and phone number (fallback only)
 * @param firstName - First name
 * @param lastName - Last name
 * @param phoneNumber - Phone number
 * @returns Generated email address
 */
export function generateDefaultEmail(firstName: string, lastName: string, phoneNumber: string): string {
  const cleanName = `${firstName.toLowerCase()}.${lastName.toLowerCase()}`.replace(/[^a-z.]/g, '');
  return `${cleanName}.${phoneNumber}@timely.vendor.com`;
}

/**
 * Transforms frontend form data to backend API format
 * @param formData - Form data from the frontend
 * @returns Transformed data for backend API
 */
export function transformFormDataToBackend(formData: VendorFormData): BackendVendorData {
  // Use provided emails or generate fallback for personal email only if needed
  const personalEmail = formData.personalEmail && formData.personalEmail.trim() !== ""
    ? formData.personalEmail
    : undefined;

  // Prepare bank accounts array
  const bankAccounts = [
    {
      account_number: formData.bankAccountNumber,
      ifsc_code: normalizeIfscCode(formData.ifscCode),
      account_holder_name: `${formData.firstName} ${formData.lastName}`,
      bank_name: formData.bankName,
      branch_name: formData.branchName,
      is_primary: true,
    }
  ];

  // Add secondary bank account if provided
  if (formData.hasSecondaryAccount &&
      formData.secondaryBankName &&
      formData.secondaryBankAccountNumber &&
      formData.secondaryIfscCode &&
      formData.secondaryBranchName) {
    bankAccounts.push({
      account_number: formData.secondaryBankAccountNumber,
      ifsc_code: normalizeIfscCode(formData.secondaryIfscCode),
      account_holder_name: `${formData.firstName} ${formData.lastName}`,
      bank_name: formData.secondaryBankName,
      branch_name: formData.secondaryBranchName,
      is_primary: false,
    });
  }

  return {
    personalDetails: {
      fname: formData.firstName,
      lname: formData.lastName,
      phone_no: formData.phoneNumber,
      email: personalEmail, // Optional field
    },
    businessDetails: {
      business_name: formData.washCenterName,
      contact_no: formData.phoneNumber,
      contact_email: formData.businessEmail, // Required field from form
      street1: formData.street1,
      street2: formData.street2,
      city: formData.city,
      state: formData.state,
      country: formData.country || "India",
      pincode: formData.pincode || "",
      longitude: parseFloat(formData.longitude || "0"),
      latitude: parseFloat(formData.latitude || "0"),
      opens_at: convertTo24Hour(formData.opensAt),
      closes_at: convertTo24Hour(formData.closesAt),
      timeslot_duration: 15, // Default 15 minutes
      simultenous_booking: 1, // Default 1 simultaneous booking
      weekly_holidays: [], // Default no weekly holidays
      holidays: [], // Default no holidays
      unavailable_timeslots: [], // Default no unavailable timeslots
    },
    kycDetails: {
      pan_no: formData.panNumber,
      aadhaar_no: formData.aadharNumber,
      gst_no: formData.gstNumber,
    },
    bankAccountDetails: bankAccounts,
  };
}

/**
 * Creates FormData object for API submission
 * @param formData - Original form data
 * @param transformedData - Transformed backend data
 * @returns FormData object ready for API submission
 */
export function createApiFormData(formData: VendorFormData, transformedData: BackendVendorData): globalThis.FormData {
  const apiFormData = new globalThis.FormData();

  // Add the JSON data
  apiFormData.append('data', JSON.stringify(transformedData));

  // Add files if they exist
  if (formData.panFile && formData.panFile instanceof File) {
    apiFormData.append('panDocument', formData.panFile);
  }

  if (formData.aadharFile && formData.aadharFile instanceof File) {
    apiFormData.append('aadhaarDocument', formData.aadharFile);
  }

  return apiFormData;
}
